import { useEffect, useState } from 'react';
import axios from '../api/axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, CircularProgress
} from '@mui/material';
import Layout from '../components/Layout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { red } from '@mui/material/colors';
import { LoadingButton } from '@mui/lab';

export default function Companies() {
  const user_id = localStorage.getItem('user_id');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', remark: '' });
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState('');
  const [submittingCompany, setSubmittingCompany] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    const params = {};
    if (searchText) params.search = searchText;
    await runFetchCompanies(params);
  };

  const runFetchCompanies = async (params = {}) => {
    setLoading(true);
    try {
      const res = await axios.get('/companies', { params: params });
      const formatted = res.data.map((c) => ({
        ...c,
        id: c.id,
        created_at: c.created_at ? dayjs(c.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
        updated_at: c.updated_at ? dayjs(c.updated_at).format('YYYY-MM-DD HH:mm:ss') : ''
      }));
      setRows(formatted);
    } finally {
      setLoading(false);
    }
  }

  const resetFilter = () => {
    // 1. Clear the UI
    setSearchText('');
    // setFilterType('');
    // setFilterSite('');
    // setFilterStartDate(null);
    // setFilterEndDate(null);

    // 2. Force a fresh fetch with no filters (not using stale state)
    runFetchCompanies({});
  };

  const handleOpen = () => {
    setEditingId(null);
    setForm({ name: '', remark: '' });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ name: '', remark: '' });
  };

  const handleEdit = (row) => {
    setForm({ name: row.name, remark: row.remark });
    setEditingId(row.id);
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setSubmittingCompany(true);
      if (editingId) {
        await axios.put(`/companies/${editingId}`, form);
      } else {
        await axios.post('/companies', form);
      }
      handleClose();
      fetchCompanies();
    } catch (e) {
      if (e.response.data.message === "Company already exists") {
        setError('公司名稱已註冊');
      } else {
        setError('提交公司資料失敗');
      }
    } finally {
      setSubmittingCompany(false);
    }
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setLoading(true);
      setDeletingCompany(true);
      await axios.put(`/companies/${confirmDeleteId}`, { active: false });
      setConfirmDeleteId(null);
      fetchCompanies();
    } catch (error) {
      alert('刪除公司失敗');
    } finally {
      setLoading(false);
      setDeletingCompany(false);
    }
  };

  const columns = [
    { field: 'name', headerName: '公司名稱', width: 240, minWidth: 200, maxWidth: 320 },
    { field: 'remark', headerName: '備註', width: 360, minWidth: 260, maxWidth: 600 },
    { field: 'created_at', headerName: '新增時間', width: 180, minWidth: 150, maxWidth: 220 },
    { field: 'updated_at', headerName: '最後修改時間', width: 180, minWidth: 150, maxWidth: 220 },
    {
      field: 'actions',
      headerName: '動作',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEdit(params.row)}><EditIcon /></IconButton>
          {user_id && user_id <= 3 && (
            <IconButton onClick={() => handleDelete(params.row.id)} sx={{ color: red[800] }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      )
    }
  ];

  return (
    <Layout>
      <Typography variant="h5" mb={2} sx={{ paddingLeft: '8px' }}>公司</Typography>

      <Box className="new-item-button-container" sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, paddingLeft: '8px' }}>
        <Button className="new-item-button" variant="contained" onClick={handleOpen}>
          新增公司
        </Button>
      </Box>

      <Box className="filter-container" sx={{ display: 'flex', gap: 1, paddingLeft: '8px', mb: 2 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="搜尋公司名稱或備註"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button variant="contained" onClick={fetchCompanies}>搜尋</Button>
        <Button variant="contained" onClick={resetFilter}>重設</Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            disableColumnMenu={true}
          />
        </Box>
      )}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editingId ? '修改公司' : '新增公司'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="公司名稱"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label="備註(選填)"
            value={form.remark}
            onChange={(e) => setForm({ ...form, remark: e.target.value })}
            margin="dense"
          />
          {error && (
            <Typography variant="body2" color="error" sx={{ ml: 1 }}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submittingCompany}>取消</Button>
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            loading={submittingCompany}
          >
            {editingId ? '更改' : '新增'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>您確定要刪除此公司嗎？</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} disabled={deletingCompany}>取消</Button>
          <LoadingButton
            onClick={confirmDelete}
            color="error"
            variant="contained"
            loading={deletingCompany}
          >
            刪除
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
